import { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import compose from 'lodash/flowRight';
import Button from '@material-ui/core/Button';
import DialogContentText from '@material-ui/core/DialogContentText';
import MaterialTextField from '@material-ui/core/TextField';
import MaterialCheckbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core/styles';
import PauseCircleFilled from '@material-ui/icons/PauseCircleFilled';
import ConfirmButton from '../confirm-button';
import { requestJoinEventMutation } from '../../apollo/events';
import consumeAppContext from '../../consumers/app';
import Markdown from '../markdown';

const useStyles = makeStyles((theme) => ({
  formControl: {
    display: 'block',
    margin: theme.spacing(1)
  },
  helperText: {
    paddingLeft: theme.spacing(4),
    marginTop: 0
  },
  prompt: {
    '& > *:first-child': {
      marginTop: 0
    }
  },
  joinMessage: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
  },
  joinIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

const RequestJoinButton = (props) => {
  const {
    event,
    requestJoinEvent,
    isLoggedIn,
    loggedInUser,
    returnTo,
    onJoin,
    login
  } = props;
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [membership, setMembership] = useState({
    phoneNumberVisibility: 'NONE',
    emailVisibility: 'NONE'
  });
  const classes = useStyles();

  if (event.membership) {
    if (event.membership.status === 'DENIED') {
      return (
        <p><strong>The host denied your join request.</strong></p>
      );
    }
    if (event.membership.status === 'REQUESTED') {
      return (
        <div className={classes.joinMessage}>
          <PauseCircleFilled className={classes.joinIcon} />
          <strong>Approval pending. You will be notified when the host responds.</strong>
        </div>
      );
    }
  }

  if (!isLoggedIn) {
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={(e) => {
          e.preventDefault();
          login(returnTo);
        }}
      >
        Log in to Join
      </Button>
    );
  }

  return (
    <ConfirmButton
      label="Request to Join"
      message="Request to Join"
      variant="contained"
      color="primary"
      onClick={() => {
        setSubmitting(true);
        requestJoinEvent({
          variables: {
            id: event.id,
            message,
            membership
          }
        }).then(() => {
          setSubmitting(false);
          onJoin();
        }).catch(() => {
          alert('An error occurred while requesting. Please try again.');
          setSubmitting(false);
        });
      }}
      disabled={submitting}
    >
      <DialogContentText className={classes.prompt}>
        <Markdown>{event.approvalPrompt || 'Include a message to the event host.'}</Markdown>
      </DialogContentText>
      <MaterialTextField
        autoFocus
        id="message"
        label="Message to Host"
        fullWidth
        multiline
        rows={3}
        maxRows={20}
        variant="outlined"
        value={message}
        onChange={(e) => {
          setMessage(e.target.value);
        }}
        disabled={submitting}
      />
      <FormControl className={classes.formControl}>
        <FormControlLabel
          control={(
            <MaterialCheckbox
              id="emailVisibility"
              onChange={(e) => {
                setMembership({
                  ...membership,
                  emailVisibility: e.target.checked ? 'HOST' : 'NONE'
                });
              }}
              value={true}
              checked={membership.emailVisibility === 'HOST'}
            />
          )}
          label="Share email with host"
          disabled={submitting}
        />
        <FormHelperText className={classes.helperText}>
          {loggedInUser.email}
        </FormHelperText>
      </FormControl>
      <FormControl className={classes.formControl}>
        <FormControlLabel
          control={(
            <MaterialCheckbox
              id="phoneNumberVisibility"
              onChange={(e) => {
                setMembership({
                  ...membership,
                  phoneNumberVisibility: e.target.checked ? 'HOST' : 'NONE'
                });
              }}
              value={true}
              checked={membership.phoneNumberVisibility === 'HOST'}
            />
          )}
          label="Share phone number with host"
          disabled={submitting}
        />
        <FormHelperText className={classes.helperText}>
          {loggedInUser.phoneNumberDisplay || 'None set (add in account preferences)'}
        </FormHelperText>
      </FormControl>
    </ConfirmButton>
  );
};

RequestJoinButton.propTypes = {
  event: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  loggedInUser: PropTypes.object,
  requestJoinEvent: PropTypes.func,
  returnTo: PropTypes.string,
  onJoin: PropTypes.func,
  login: PropTypes.func
};

export default compose(
  consumeAppContext,
  graphql(requestJoinEventMutation, {
    name: 'requestJoinEvent'
  })
)(RequestJoinButton);